import React from 'react';
import { plural } from './api';

const CHOICE_LABEL = {
    copy: "Copiar",
    link: "Vincular con el existente",
    pending: "Dejar pendiente",
};

const TABLE_LABEL = { formats: "Formato", task_boards: "Tablero" };

// Nombres de las tablas de catálogo en cristiano. La tabla cruda se deja como respaldo para los
// catálogos que se agreguen después y todavía no tengan nombre aquí.
const CATALOG_LABEL = {
    positions: "Cargos",
    procesos: "Procesos",
    users: "Usuarios",
    icons: "Iconos",
    section_formats: "Permisos de módulo por cargo",
    section_custom_views: "Permisos de vistas por cargo",
    formats_permissions: "Permisos sobre formatos",
    custom_view_permissions: "Permisos sobre vistas",
    health_related_events: "Eventos de salud",
    health_specific_areas: "Áreas de salud",
};

// Un título en español para cada aviso del plan. El mensaje del servidor es preciso pero técnico;
// el título dice de qué va antes de leerlo. `tecnico` manda el aviso al final de la lista: son los
// que solo le sirven a quien programa.
const WARNING_LABEL = {
    origen_ajeno: { titulo: "Datos de otra empresa" },
    catalogo_sin_coincidencia: { titulo: "Sin pareja en la empresa destino" },
    fila_inversa: { titulo: "Filas que se quedan fuera" },
    tablero_descartado: { titulo: "Tablero que no se copia" },
    secreto_cross_tenant: { titulo: "Credenciales que no viajan" },
    origen_roto: { titulo: "El original ya venía roto" },
    huerfana_origen: { titulo: "Filas sueltas del origen" },
    destino_sin_conexion: { titulo: "Sin empresa destino" },
    plantilla_vieja: { titulo: "Plantilla del duplicador viejo" },
    snapshot: { titulo: "Detalle técnico", tecnico: true },
};

const avisoTitulo = (code) => (WARNING_LABEL[code] || {}).titulo;
const esTecnico = (code) => ((WARNING_LABEL[code] || {}).tecnico === true);

const choiceText = (d, choice) => {
    if (choice === "link") return d.target_name ? `Vincular con «${d.target_name}»` : CHOICE_LABEL.link;
    return CHOICE_LABEL[choice] || choice;
};

// Tarjeta de una decisión: un formato o tablero FUERA de la selección al que algo de la copia
// apunta. El plan recomienda (vincular si existe, si no copiar) y el usuario puede cambiarlo.
const DecisionCard = ({ d, value, onChange }) => {
    const current = value || d.choice;
    const overridden = current !== d.recommended;
    return (
        <div className={`sd-decision ${current === "pending" ? "is-pending" : ""} ${overridden ? "is-overridden" : ""}`}>
            <div>
                <div className="sd-decision__name">
                    <span className="sd-badge sd-badge--muted">{TABLE_LABEL[d.unit_table] || d.unit_table}</span>
                    {d.name}
                    {d.inexact && <span className="sd-badge sd-badge--warn">coincidencia inexacta</span>}
                    {overridden && <span className="sd-badge sd-badge--info">cambiada</span>}
                </div>
                <div className="sd-decision__reason">{d.reason}</div>
                {current === "pending" && (
                    <div className="sd-decision__reason" style={{ color: "#d97706" }}>
                        <i className="fas fa-exclamation-triangle"></i> Pendiente: lo que apunta aquí queda vacío (o no se copia). Un subformulario quedaría roto.
                    </div>
                )}
                {d.edges && d.edges.length > 0 && (
                    <div className="sd-decision__edges">Lo usan: {d.edges.join(" · ")}</div>
                )}
            </div>
            <div>
                <select className="rk-input" value={current} onChange={(e) => onChange(d.id, e.target.value)}>
                    {d.options.map((o) => (
                        <option key={o} value={o}>{choiceText(d, o)}{o === d.recommended ? " (recomendado)" : ""}</option>
                    ))}
                </select>
            </div>
        </div>
    );
};

const Review = ({ plan, planning, dirty, decisions, onChange, onReplan }) => {
    if (planning && !plan) {
        return <div className="sd-card"><div className="rk-empty"><i className="fas fa-spinner fa-spin"></i><p>Armando el plan: leyendo el origen y el destino…</p></div></div>;
    }
    if (!plan) return null;

    const blockers = plan.validaciones.filter((v) => v.level === "blocking");
    const warnings = plan.validaciones.filter((v) => v.level !== "blocking");
    const counts = plan.conteos || {};
    const catalogos = plan.catalogos.filter((c) => !c.skipped);
    // Un catálogo que la copia no usa (0 valores) no dice nada: se nombran juntos al final.
    const catalogosUsados = catalogos.filter((c) => c.used > 0);
    const catalogosVacios = catalogos.filter((c) => c.used === 0);
    const missingCatalogs = catalogosUsados.filter((c) => c.entries.some((e) => e.estado !== "ok"));
    const pendings = plan.decisiones.filter((d) => (decisions[d.id] || d.choice) === "pending").length;
    // Las plantillas viejas que algo de la copia APUNTA: esas no son ruido, avisan de un campo que
    // va a quedar vacío en la copia.
    const plantillasApuntadas = plan.plantillas_viejas.filter((t) => t.situacion === "referenciada");
    // Lo técnico, al final: se lee primero lo que le importa a quien está copiando.
    const avisosOrdenados = plan.avisos.slice().sort((a, b) => Number(esTecnico(a.code)) - Number(esTecnico(b.code)));

    return (
        <div>
            {dirty && (
                <div className="sd-card" style={{ borderColor: "#99c2ff", background: "#f5f9ff" }}>
                    <div className="sd-footer" style={{ marginTop: 0 }}>
                        <span><i className="fas fa-sync-alt" style={{ color: "#0061fe" }}></i> Cambiaste decisiones: vuelve a planear para ver cómo queda la copia.</span>
                        <button type="button" className="rk-btn rk-btn--primary" disabled={planning} onClick={onReplan}>
                            {planning ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-sync-alt"></i>} Volver a planear
                        </button>
                    </div>
                </div>
            )}

            <p className="sd-muted" style={{ margin: "0 0 8px" }}>
                <i className="fas fa-info-circle"></i> Un «elemento» es cada pieza de configuración que se copia: cada pregunta, cada permiso de un cargo, cada PDF, cada fórmula. Por eso el número es mucho más grande que la cantidad de formatos.
            </p>
            <div className="sd-stats">
                <div className="sd-stat"><div className="sd-stat__value">{plan.total}</div><div className="sd-stat__label">elementos a copiar</div></div>
                <div className="sd-stat"><div className="sd-stat__value">{counts.format_categories || 0}</div><div className="sd-stat__label">módulos / carpetas</div></div>
                <div className="sd-stat"><div className="sd-stat__value">{counts.formats || 0}</div><div className="sd-stat__label">formatos</div></div>
                <div className="sd-stat"><div className="sd-stat__value">{counts.questions || 0}</div><div className="sd-stat__label">preguntas</div></div>
                <div className={`sd-stat ${pendings ? "sd-stat--warn" : ""}`}><div className="sd-stat__value">{plan.decisiones.length}</div>
                    <div className="sd-stat__label">decisiones{pendings ? ` · ${pendings} pendiente(s)` : ""}</div></div>
                <div className={`sd-stat ${blockers.length ? "sd-stat--error" : "sd-stat--ok"}`}><div className="sd-stat__value">{blockers.length ? blockers.length : <i className="fas fa-check"></i>}</div>
                    <div className="sd-stat__label">{blockers.length ? "bloqueos" : "sin bloqueos"}</div></div>
            </div>

            {blockers.length > 0 && (
                <div className="sd-card" style={{ borderColor: "#fecaca" }}>
                    <h3 className="sd-card__title" style={{ color: "#dc2626" }}><i className="fas fa-ban" style={{ color: "#dc2626" }}></i> No se puede copiar todavía</h3>
                    <ul className="sd-list">
                        {blockers.map((v) => (
                            <li key={v.code} className="is-error"><i className="fas fa-times-circle"></i>
                                <div>{v.message}{v.hint && <small>{v.hint}</small>}</div></li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="sd-card">
                <h3 className="sd-card__title"><i className="fas fa-project-diagram"></i> Decisiones</h3>
                <p className="sd-card__hint">
                    Formatos y tableros que están <b>fuera</b> de lo que marcaste pero que la copia usa (subformularios, campos tipo formato, asociaciones, plantillas).
                    Los que se copian entran en la carpeta raíz de la copia.
                </p>
                {plan.decisiones.length === 0
                    ? <p className="sd-muted"><i className="fas fa-check-circle" style={{ color: "#059669" }}></i> Nada fuera de la selección: la copia es autocontenida.</p>
                    : plan.decisiones.map((d) => (
                        <DecisionCard key={d.id} d={d} value={decisions[d.id]} onChange={onChange} />
                    ))}
            </div>

            {plan.plantillas_viejas.length > 0 && (
                <details className="sd-details" open={plantillasApuntadas.length > 0}>
                    <summary>
                        <i className="fas fa-trash-alt" style={{ color: "#94a3b8" }}></i> {plural(plan.plantillas_viejas.length, "plantilla vieja", "plantillas viejas")} que NO se copian
                        {plantillasApuntadas.length > 0 && (
                            <span className="sd-badge sd-badge--warn" style={{ marginLeft: 8 }}>{plantillasApuntadas.length} en uso</span>
                        )}
                    </summary>
                    <div className="sd-details__body">
                        <p className="sd-muted" style={{ marginTop: 0 }}>
                            Formatos vacíos que dejó el duplicador viejo cuando no podía copiar algo («TEMPLATE - Cambiar Manualmente»).
                            El Super Duplicador nunca los copia. Abajo, por qué aparece cada uno.
                        </p>
                        <ul className="sd-list">
                            {plan.plantillas_viejas.map((t) => (
                                <li key={t.id} className={t.situacion === "referenciada" ? "is-warn" : "is-info"}>
                                    <i className={t.situacion === "referenciada" ? "fas fa-exclamation-triangle" : "fas fa-file"}></i>
                                    <div>
                                        <b>{t.name}</b>
                                        <small>
                                            {t.situacion === "referenciada" && (
                                                <React.Fragment>
                                                    <b>La usa algo que sí se copia</b> ({t.edges.join(" · ")}). Como no se copia, ese campo queda
                                                    vacío en la copia. Revisa en el original qué debería apuntar ahí.
                                                </React.Fragment>
                                            )}
                                            {t.situacion === "arrastrada" && "Está dentro de un módulo que marcaste, pero se queda fuera de la copia."}
                                            {t.situacion === "pedida" && "La marcaste a mano en la selección, pero tampoco se copia."}
                                        </small>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </details>
            )}

            <details className="sd-details" open={missingCatalogs.length > 0}>
                <summary>
                    <i className="fas fa-id-badge" style={{ color: "#0061fe" }}></i> Cargos, procesos y usuarios de la empresa destino
                    {missingCatalogs.length > 0
                        ? <span className="sd-badge sd-badge--warn" style={{ marginLeft: 8 }}>{missingCatalogs.length} sin pareja</span>
                        : <span className="sd-badge sd-badge--ok" style={{ marginLeft: 8 }}>todo tiene pareja</span>}
                </summary>
                <div className="sd-details__body">
                    <p className="sd-muted" style={{ marginTop: 0 }}>
                        La copia no trae los cargos ni los usuarios del origen: los busca por nombre en la empresa destino.
                        Aquí ves qué encontró.
                    </p>
                    <ul className="sd-list">
                        {catalogosUsados.map((c) => {
                            const missing = c.entries.filter((e) => e.estado !== "ok");
                            return (
                                <li key={c.table} className={missing.length ? "is-warn" : "is-ok"}>
                                    <i className={missing.length ? "fas fa-exclamation-triangle" : "fas fa-check-circle"}></i>
                                    <div>
                                        <b>{CATALOG_LABEL[c.table] || c.table}</b>: {c.matched} de {c.used} con pareja en el destino
                                        {missing.length > 0 && (
                                            <small>
                                                Sin pareja: {missing.slice(0, 8).map((e) => e.label || `#${e.id}`).join(" · ")}{missing.length > 8 ? ` … y ${missing.length - 8} más` : ""}
                                                {c.creatable
                                                    ? " — se crean en el destino."
                                                    : " — donde se usen, la copia pone al dueño de respaldo."}
                                            </small>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {catalogosVacios.length > 0 && (
                        <p className="sd-muted" style={{ marginBottom: 0 }}>
                            No se usan en esta copia: {catalogosVacios.map((c) => CATALOG_LABEL[c.table] || c.table).join(" · ")}.
                        </p>
                    )}
                </div>
            </details>

            {(warnings.length > 0 || plan.avisos.length > 0) && (
                <details className="sd-details">
                    <summary><i className="fas fa-info-circle" style={{ color: "#d97706" }}></i> {plural(warnings.length + plan.avisos.length, "aviso", "avisos")}</summary>
                    <div className="sd-details__body">
                        <p className="sd-muted" style={{ marginTop: 0 }}>Ninguno impide copiar. Son cosas que conviene saber antes.</p>
                        <ul className="sd-list">
                            {warnings.map((v) => (
                                <li key={`v-${v.code}`} className="is-warn"><i className="fas fa-exclamation-triangle"></i>
                                    <div>{v.message}{v.hint && <small>{v.hint}</small>}</div></li>
                            ))}
                            {avisosOrdenados.map((w, i) => (
                                <li key={`w-${i}`} className={esTecnico(w.code) ? "is-info sd-muted" : "is-info"}><i className="fas fa-info-circle"></i>
                                    <div>
                                        {avisoTitulo(w.code) && <b>{avisoTitulo(w.code)}: </b>}
                                        {w.message}{w.hint && <small>{w.hint}</small>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </details>
            )}
        </div>
    );
};

export default Review;
